import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CodeBlock } from "./CodeBlock";
import { CodeSamples } from "./CodeSamples";
import { RequestPanel } from "./RequestPanel";
import { EndpointReferenceProps } from "../interfaces";
import { buildCodeSamples } from "../utils";

export const EndpointReference: FC<EndpointReferenceProps> = (props) => {
  const { endpoint } = props;
  const { t } = useTranslation();
  // The five snippets come from the endpoint definition, so they are rebuilt only when it changes.
  const samples = useMemo(() => {
    return buildCodeSamples(endpoint);
  }, [endpoint]);

  return (
    <section className="space-y-5 border-t border-gray-200 pt-10">
      <div className="space-y-2">
        {/* The operationId is the anchor, so a link to one endpoint of the reference keeps working
            even if the endpoints are reordered. YOSHI-10464: it sits on the title and not on the
            section, as it does on every other page of the portal, so that a jump leaves the title
            of the operation exactly under the header instead of the blank the separator adds. */}
        <h3 id={endpoint.id} className="scroll-mt-6 text-lg font-bold lg:text-xl">
          {t(`docs.operations.${endpoint.id}.title`)}
        </h3>
        <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
          {t(`docs.operations.${endpoint.id}.description`)}
        </p>
        <p className="text-sm text-gray-500">
          <span className="font-semibold">{t("docs.common.scope")}: </span>
          {endpoint.scope !== null ? (
            <code className="font-mono text-primary">{endpoint.scope}</code>
          ) : (
            t("docs.common.tokenOnly")
          )}
        </p>
      </div>

      <RequestPanel endpoint={endpoint} />

      <div className="space-y-3">
        <h4 className="text-sm font-bold uppercase tracking-wide text-gray-500">
          {t("docs.common.codeExamples")}
        </h4>
        <CodeSamples samples={samples} />
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-bold uppercase tracking-wide text-gray-500">
          {t("docs.common.response")}
        </h4>
        <CodeBlock
          code={endpoint.response}
          language="json"
          syntax="json"
        />
      </div>
    </section>
  );
};
